import React, { useState } from 'react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  useToast,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { deleteResearch } from '../../utils/researchUtils'

interface DeleteResearchDialogProps {
  isOpen: boolean
  onClose: () => void
  researchId: string
  researchTitle: string
}

const DeleteResearchDialog: React.FC<DeleteResearchDialogProps> = ({
  isOpen,
  onClose,
  researchId,
  researchTitle,
}) => {
  const [deleting, setDeleting] = useState(false)
  const cancelRef = React.useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()
  const toast = useToast()

  const handleDelete = async () => {
    try {
      setDeleting(true)
      await deleteResearch(researchId)

      toast({
        title: 'Research Deleted',
        description: 'The research article has been successfully deleted',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })

      navigate('/research')
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete research article',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setDeleting(false)
      onClose()
    }
  }

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize='lg' fontWeight='bold'>
            Delete Research Article
          </AlertDialogHeader>

          <AlertDialogBody>
            Are you sure you want to delete "{researchTitle}"? This action
            cannot be undone.
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme='red'
              onClick={handleDelete}
              isLoading={deleting}
              loadingText='Deleting...'
              ml={3}
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

export default DeleteResearchDialog
